import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Card } from "react-bootstrap";
import {
  HTTP_STATUS,
  S_APPLICANT_ID,
  S_APPLICANT_MEMBER_NO,
  S_CURRENT_MEMBER_ID,
  S_STAFF_ID,
} from "../Communication/Constants";
import {
  getCurrentStepIndexFromTemplateFlowByStep,
  getDescriptionFromArray,
  getProductInfoFromCurrentDoximProducts,
} from "../Utils/CommonUtilities";
import {
  getFileIconFromName,
  getFileTypeFromName,
  formatFileDateTime,
} from "../Utils/FileExtensionHelpers";
import PopupModal from "../Components/Common/PopupModal/PopupModal";
import OtpModal from "./OtpModal";
import AppConstants from "../Constants/AppConstants";
import { checkTaxResidence } from "../Utils/LogicUtilities";
import { getPaysafeDetails } from "../Redux/Actions/PaysafeAction";
import { doGetStaffList } from "../Redux/Actions/StaffAction";
import * as ROUTES from "../Constants/Routes";
import { getProductTemplate } from "../Redux/Actions/ProductTemplateActions";

const ApplicationDetails = ({
  application,
  onBack,
  productList,
  doAddDocument,
  doDeleteDocument,
  doGetDocumentList,
  doGetDocument,
  applicationProperty,
  applicant,
  employmentAndIncomeReducer,
  complianceReducer,
  selectedProductInfo,
  finInfo,
  doGetApplicationNotes,
  doUpdateApplicationNotesReview,
  doDeleteApplication,
  doConfirmApp,
  doGetProductFromVault,
  doUpdateProductToVault,
  doGetVault,
  history,
  doGetApplicationProperty,
  selectedMember,
  onApplicationResubmitted,
  handleBreadcrumbBackToApplications,
  updateUrlParams,
  setMemberApplicationsTab,
  setShowMemberDetails,
}) => {
  console.log(
    "ApplicationDetails",
    application,
    onBack,
    productList,
    applicationProperty,
    applicant
  );

  const {
    hasUsaTaxResidence,
    hasOtherCountryTaxResidence,
    hasOtherTaxResidence,
    oecdTaxResidency,
    oecdTaxId,
  } = applicant
    ? checkTaxResidence(applicant)
    : {
        hasUsaTaxResidence: false,
        hasOtherCountryTaxResidence: false,
        hasOtherTaxResidence: false,
        oecdTaxResidency: null,
        oecdTaxId: null,
      };

  // Helper function to safely get nested values
  const getNestedValue = (obj, path, defaultValue = "—") => {
    if (!obj) return defaultValue;
    const value = path.split(".").reduce((acc, part) => acc?.[part], obj);
    return value !== null && value !== undefined && value !== ""
      ? value
      : defaultValue;
  };

  // Helper function to format date
  const formatDate = (dateValue) => {
    if (!dateValue || dateValue === "—") return "—";
    try {
      const date = new Date(dateValue);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateValue;
    }
  };

  // Helper function to calculate relative time (e.g., "1 day ago", "2 hours ago")
  const getRelativeTime = (timestamp) => {
    if (!timestamp) return "—";

    try {
      // Convert to milliseconds if timestamp is in seconds (10 digits)
      const timestampMs =
        timestamp.toString().length === 10 ? timestamp * 1000 : timestamp;
      const date = new Date(timestampMs);
      const now = new Date();
      const diffMs = now - date;

      // Convert to different time units
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);
      const diffWeeks = Math.floor(diffDays / 7);
      const diffMonths = Math.floor(diffDays / 30);
      const diffYears = Math.floor(diffDays / 365);

      if (diffYears > 0) {
        return `${diffYears} year${diffYears > 1 ? "s" : ""} ago`;
      } else if (diffMonths > 0) {
        return `${diffMonths} month${diffMonths > 1 ? "s" : ""} ago`;
      } else if (diffWeeks > 0) {
        return `${diffWeeks} week${diffWeeks > 1 ? "s" : ""} ago`;
      } else if (diffDays > 0) {
        return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
      } else if (diffMinutes > 0) {
        return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
      } else {
        return "Just now";
      }
    } catch {
      return "—";
    }
  };

  // Helper function to format phone number
  const formatPhone = (phone) => {
    if (!phone) return "—";
    const cleaned = phone.toString().replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    } else if (cleaned.length === 11) {
      return `+${cleaned.slice(0, 1)} (${cleaned.slice(1, 4)}) ${cleaned.slice(
        4,
        7
      )}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  // Helper function to format address
  const formatAddress = (address) => {
    if (!address) return "—";
    const parts = [
      address.currentAddressLine1,
      address.currentAddressCity,
      address.currentAddressProvince,
      address.currentAddressPostalCode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "—";
  };

  const formatMailAddress = (address) => {
    if (!address) return "—";
    const parts = [
      address.mailingAddressLine1,
      address.mailingCity,
      address.mailingProvince,
      address.mailingPostalCode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "—";
  };

  // State for storing document list
  const [documentList, setDocumentList] = useState([]);

  // State for success modal
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // State for delete success modal
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false);

  // State for escalate success modal
  const [showEscalateSuccessModal, setShowEscalateSuccessModal] = useState(
    false
  );

  // State for application submitted modal
  const [
    showApplicationSubmittedModal,
    setShowApplicationSubmittedModal,
  ] = useState(false);

  // Handler for closing application submitted modal and refreshing
  const handleApplicationSubmittedModalClose = () => {
    setShowApplicationSubmittedModal(false);

    const applicationId = application.id || application.applicationId;
    if (!doGetApplicationProperty || !applicationId) return;

    // Refresh the application property first
    doGetApplicationProperty({ applicationId }, (refreshedProperty) => {
      console.log(
        "Application property refreshed after submission:",
        refreshedProperty
      );

      // Refresh the application notes next
      setApplicationNotesLoading(true);
      doGetApplicationNotes(applicationId, (result) => {
        console.log(
          "ApplicationDetails - doGetApplicationNotes callback result:",
          result
        );
        setApplicationNotes(result?.data || result || []);
        setApplicationNotesLoading(false);

        // After both property and notes are refreshed, make sure the Applications tab is active
        if (selectedMember?.accountId) {
          updateUrlParams(selectedMember.accountId); // Update URL for this member
          setMemberApplicationsTab("applications"); // Force Applications tab
          setShowMemberDetails(true); // Show the member details panel
        }
      });
    });
  };

  // File viewer modal state
  const [fileViewerModalOpen, setFileViewerModalOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  const [fileViewerUrl, setFileViewerUrl] = useState(null);

  // Hidden file input ref for direct file selection
  const fileInputRef = useRef(null);

  // State for showing detailed review view
  const [showDetailedView, setShowDetailedView] = useState(false);

  // State for expanded sections in application details
  const [expandedSections, setExpandedSections] = useState({});

  // State for active tab
  const [activeTab, setActiveTab] = useState("productDetails");

  // State for paysafe details
  const [paysafeDetails, setPaysafeDetails] = useState([]);
  const [paysafeLoading, setPaysafeLoading] = useState(false);

  // State for application notes (items for review)
  const [applicationNotes, setApplicationNotes] = useState([]);
  const [applicationNotesLoading, setApplicationNotesLoading] = useState(false);

  // State for review item selections and notes (main view)
  const [reviewSelections, setReviewSelections] = useState({});
  const [reviewNotes, setReviewNotes] = useState({});

  // State for modal review selections and notes (separate from main state)
  const [modalReviewSelections, setModalReviewSelections] = useState({});
  const [modalReviewNotes, setModalReviewNotes] = useState({});

  // State for expanded notes
  const [expandedNotes, setExpandedNotes] = useState({});

  // State for escalation options
  const [escalationMethod, setEscalationMethod] = useState({});
  const [selectedColleague, setSelectedColleague] = useState({});
  const [escalationNote, setEscalationNote] = useState({});

  // State for storing the escalated colleague name for the success modal
  const [escalatedColleagueName, setEscalatedColleagueName] = useState("");

  // State for in-person verification code modal
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationErrors, setVerificationErrors] = useState({});

  // State for update notes modal (shown after verification)
  const [showUpdateNotesModal, setShowUpdateNotesModal] = useState(false);

  // Redux dispatch and selectors
  const dispatch = useDispatch();
  const staffList = useSelector((state) => {
    const data = state.StaffReducer.staffList;
    // Handle case where staffList might be an object with a staffList property
    if (data && Array.isArray(data.staffList)) {
      return data.staffList;
    }
    // Handle case where staffList is already an array
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  });

  // Handler for button selection (Uphold/Override/Escalate)
  const handleReviewSelection = (noteId, selection) => {
    setReviewSelections((prev) => ({
      ...prev,
      [noteId]: selection,
    }));

    // Reset escalation options when selection changes
    if (selection !== "escalate") {
      setEscalationMethod((prev) => {
        const updated = { ...prev };
        delete updated[noteId];
        return updated;
      });
      setSelectedColleague((prev) => {
        const updated = { ...prev };
        delete updated[noteId];
        return updated;
      });
      setEscalationNote((prev) => {
        const updated = { ...prev };
        delete updated[noteId];
        return updated;
      });
    }
  };

  // Handler for note textarea change
  const handleReviewNoteChange = (noteId, value) => {
    setReviewNotes((prev) => ({
      ...prev,
      [noteId]: value,
    }));
  };

  // Check if Update button should be enabled for a specific note
  const isUpdateEnabled = (noteId) => {
    const selection = reviewSelections[noteId];

    // For escalate, check if escalation method and colleague are selected
    // Note is only required if escalation method is not "in-person"
    if (selection === "escalate") {
      const hasRequiredFields = !!(
        escalationMethod[noteId] && selectedColleague[noteId]
      );

      // If escalation method is "in-person", note is not required
      if (escalationMethod[noteId] === "in-person") {
        return hasRequiredFields;
      }

      // For other escalation methods, note is required
      return hasRequiredFields && escalationNote[noteId]?.trim();
    }

    // For uphold/override, just check if note is provided
    return !!(selection && reviewNotes[noteId]?.trim());
  };

  // Handler for Update button click
  const handleUpdateReview = (noteId) => {
    const selection = reviewSelections[noteId];
    const note = reviewNotes[noteId];
    console.log("handleUpdateReview", selection, note);
    if (!doUpdateApplicationNotesReview) {
      console.error("doUpdateApplicationNotesReview function not provided");
      return;
    }

    // Create payload with reviewCode and reviewNotes
    const payload = {
      reviewCode: selection.toUpperCase(),
      reviewNotes: selection === "escalate" ? "" : note,
    };

    // Add assignType and assignNotes fields when escalating
    if (selection === "escalate") {
      payload.assignTo = selectedColleague[noteId];
      payload.assignType = escalationMethod[noteId];
      payload.assignNotes = escalationNote[noteId];
      payload.reviewBy = selectedColleague[noteId];
    }

    console.log("Updating review for note:", noteId, payload);

    // Call API to update the note with reviewCode and reviewNotes
    doUpdateApplicationNotesReview(noteId, payload, (response) => {
      console.log("Update review response:", response);

      // Refresh application notes after successful update
      if (
        doGetApplicationNotes &&
        (application.id || application.applicationId)
      ) {
        doGetApplicationNotes(
          application.id || application.applicationId,
          (result) => {
            setApplicationNotes(result?.data || result || []);

            // Clear the selections and notes for this item
            setReviewSelections((prev) => {
              const updated = { ...prev };
              delete updated[noteId];
              return updated;
            });
            setReviewNotes((prev) => {
              const updated = { ...prev };
              delete updated[noteId];
              return updated;
            });
            setEscalationMethod((prev) => {
              const updated = { ...prev };
              delete updated[noteId];
              return updated;
            });
            setSelectedColleague((prev) => {
              const updated = { ...prev };
              delete updated[noteId];
              return updated;
            });
            setEscalationNote((prev) => {
              const updated = { ...prev };
              delete updated[noteId];
              return updated;
            });

            // Trigger parent refresh if callback provided
            if (onBack && typeof onBack === "function") {
              // Signal parent to refresh application details
              console.log("Application notes updated, parent should refresh");
            }
          }
        );
      }
    });
  };

  // Helper function to get colleague display name from value
  const getColleagueDisplayName = (value) => {
    console.log("getColleagueDisplayName", value);
    const staff = staffList.find((s) => s.id === value);
    if (staff) {
      const firstName = staff.name ? staff.name.split(" ")[0] : staff.name;
      return `${firstName}${staff.role ? ` - ${staff.role}` : ""}`;
    }
    return value;
  };

  // Handler for escalate application button
  const handleEscalateApplication = () => {
    if (!doUpdateApplicationNotesReview) {
      console.error("doUpdateApplicationNotesReview function not provided");
      return;
    }
    console.log("handleEscalateApplication", reviewSelections);
    // Check if any escalation method is "in-person"
    const hasInPersonEscalation = Object.keys(reviewSelections).some(
      (noteId) =>
        reviewSelections[noteId] === "escalate" &&
        escalationMethod[noteId] === "in-person"
    );

    // If in-person escalation exists, show verification modal
    if (hasInPersonEscalation) {
      setShowVerificationModal(true);
      return;
    }

    // Otherwise, proceed with escalation
    proceedWithEscalation();
  };

  // Function to proceed with escalation after verification
  const proceedWithEscalation = () => {
    // Find all notes that need to be escalated (current selections with escalate)
    console.log("proceedWithEscalation");
    const notesToEscalate = [];
    let firstColleagueName = "";

    for (const noteId in reviewSelections) {
      if (reviewSelections[noteId] === "escalate") {
        // Check if escalation method, colleague, and note are all filled
        if (
          escalationMethod[noteId] &&
          selectedColleague[noteId] &&
          escalationNote[noteId]?.trim()
        ) {
          // Store the first colleague name for the success modal
          if (!firstColleagueName) {
            firstColleagueName = getColleagueDisplayName(
              selectedColleague[noteId]
            );
          }

          notesToEscalate.push({
            noteId,
            payload: {
              reviewCode: "ESCALATE",
              reviewNotes: "",
              assignTo: selectedColleague[noteId],
              assignType: escalationMethod[noteId],
              assignNotes: escalationNote[noteId],
              reviewBy: selectedColleague[noteId],
            },
          });
        }
      }
    }

    // If there are notes to escalate, update them
    if (notesToEscalate.length > 0) {
      let completedUpdates = 0;
      const totalUpdates = notesToEscalate.length;
      console.log("notes to escalate ", notesToEscalate.length);
      notesToEscalate.forEach(({ noteId, payload }) => {
        doUpdateApplicationNotesReview(noteId, payload, (response) => {
          console.log("Escalate update response:", response);
          completedUpdates++;
          console.log("escalate status", completedUpdates, totalUpdates);
          // When all updates are complete, refresh and show success modal
          if (completedUpdates === totalUpdates) {
            // Refresh application notes
            if (
              doGetApplicationNotes &&
              (application.id || application.applicationId)
            ) {
              doGetApplicationNotes(
                application.id || application.applicationId,
                (result) => {
                  setApplicationNotes(result?.data || result || []);

                  // Clear all escalation-related state
                  setReviewSelections({});
                  setReviewNotes({});
                  setEscalationMethod({});
                  setSelectedColleague({});
                  setEscalationNote({});

                  // Set the colleague name and show success modal
                  setEscalatedColleagueName(firstColleagueName);
                  setShowEscalateSuccessModal(true);
                }
              );
            } else {
              // Set the colleague name and show success modal even if refresh fails
              setEscalatedColleagueName(firstColleagueName);
              setShowEscalateSuccessModal(true);
            }
          }
        });
      });
    } else {
      // If no new notes to escalate, just show the success modal
      // (this handles the case where notes are already escalated)
      setEscalatedColleagueName(firstColleagueName || "the selected colleague");
      setShowEscalateSuccessModal(true);
    }
  };

  // Handler for verification submit
  const handleVerificationSubmit = (code) => {
    // Validate code is 6 digits
    if (code.length !== 6) {
      setVerificationErrors({
        authCode: ["Please enter a 6-digit code"],
      });
      return;
    }

    // Close verification modal and show update notes modal
    setShowVerificationModal(false);
    setVerificationErrors({});
    setShowUpdateNotesModal(true);
  };

  // Handler for verification modal close
  const handleVerificationClose = () => {
    setShowVerificationModal(false);
    setVerificationErrors({});
  };

  // Get colleague name for verification modal
  const getVerificationColleagueName = () => {
    for (const noteId in reviewSelections) {
      if (
        reviewSelections[noteId] === "escalate" &&
        escalationMethod[noteId] === "in-person" &&
        selectedColleague[noteId]
      ) {
        return getColleagueDisplayName(selectedColleague[noteId]);
      }
    }
    return "";
  };

  const allNotesUphold = () => {
    if (!applicationNotes || applicationNotes.length === 0) {
      return true;
    }
    return applicationNotes
      .filter((note) => note.noteType !== "SUBMIT")
      .every((note) => note.reviewCode === "UPHOLD");
  };

  // Check if all application notes have a reviewCode
  const allNotesReviewed = () => {
    if (!applicationNotes || applicationNotes.length === 0) {
      return true;
    }
    return applicationNotes
      .filter((note) => note.noteType !== "SUBMIT")
      .every((note) => note.reviewCode === "OVERRIDE");
  };

  // Check if any application note has reviewCode === "escalate" OR current screen has escalate selected with all required fields
  const hasEscalatedNote = () => {
    // Check if current screen has escalate selected with all required fields filled
    for (const noteId in reviewSelections) {
      if (reviewSelections[noteId] === "escalate") {
        // Check if escalation method and colleague are filled
        const hasRequiredFields = !!(
          escalationMethod[noteId] && selectedColleague[noteId]
        );

        // If escalation method is "in-person", note is not required
        if (escalationMethod[noteId] === "in-person") {
          if (hasRequiredFields) {
            return true;
          }
        } else {
          // For other escalation methods, note is required
          if (hasRequiredFields && escalationNote[noteId]?.trim()) {
            return true;
          }
        }
      }
    }

    return false;
  };

  // Handler for reject application button
  const handleRejectApplication = () => {
    if (!doDeleteApplication) {
      console.error("doDeleteApplication function not provided");
      return;
    }

    const applicationId = application.id || application.applicationId;
    if (!applicationId) {
      console.error("No application ID found");
      return;
    }

    console.log("Rejecting application:", applicationId);
    doDeleteApplication(applicationId, (response) => {
      console.log("Delete application response:", response);
      // Navigate back to the application list and trigger refresh
      if (onBack) {
        onBack(true);
      }
    });
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Toggle note expansion
  const toggleNoteExpansion = (noteId) => {
    setExpandedNotes((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }));
  };

  // Function to handle document view in modal
  const handleDownloadDocument = (doc) => {
    console.log("handleDownloadDocument", doc);
    if (doGetDocument) {
      doGetDocument(doc.memberNumber, doc.documentId, (response) => {
        if (response && response.data) {
          // Determine the correct MIME type based on file extension
          const extension = doc.documentName?.split(".").pop()?.toLowerCase();
          let mimeType =
            response.headers["content-type"] || "application/octet-stream";

          // Override MIME type if needed based on file extension
          if (extension === "pdf" && !mimeType.includes("pdf")) {
            mimeType = "application/pdf";
          } else if (
            ["jpg", "jpeg"].includes(extension) &&
            !mimeType.includes("image")
          ) {
            mimeType = "image/jpeg";
          } else if (extension === "png" && !mimeType.includes("image")) {
            mimeType = "image/png";
          } else if (extension === "gif" && !mimeType.includes("image")) {
            mimeType = "image/gif";
          } else if (extension === "bmp" && !mimeType.includes("image")) {
            mimeType = "image/bmp";
          }

          // Create a blob URL from the response data with correct MIME type
          const blob = new Blob([response.data], { type: mimeType });
          const url = window.URL.createObjectURL(blob);

          // Open the file viewer modal
          setViewingFile(doc);
          setFileViewerUrl(url);
          setFileViewerModalOpen(true);
        }
      });
    }
  };

  // Function to handle file download
  const handleFileDownload = (doc) => {
    if (doGetDocument) {
      doGetDocument(doc.memberNumber, doc.documentId, (response) => {
        if (response && response.data) {
          // Create a blob URL from the response data
          const blob = new Blob([response.data], {
            type:
              response.headers["content-type"] || "application/octet-stream",
          });
          const url = window.URL.createObjectURL(blob);

          // Create a temporary link element to trigger download
          const link = document.createElement("a");
          link.href = url;
          link.download = doc.documentName || "download";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up the blob URL
          setTimeout(() => window.URL.revokeObjectURL(url), 100);
        }
      });
    }
  };

  // Function to handle file printing
  const handlePrintFile = () => {
    if (fileViewerUrl) {
      // Create a hidden iframe for printing
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = fileViewerUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      };
    }
  };

  // Function to close file viewer modal
  const closeFileViewer = () => {
    if (fileViewerUrl) {
      window.URL.revokeObjectURL(fileViewerUrl);
    }
    setFileViewerModalOpen(false);
    setViewingFile(null);
    setFileViewerUrl(null);
  };

  // Function to handle document deletion
  const handleDeleteDocument = (doc) => {
    if (doDeleteDocument) {
      const applicationId = application.id || application.applicationId;

      doDeleteDocument(applicationId, doc.documentId, (res) => {
        // Re-query document list after successful deletion
        if (doGetDocumentList) {
          doGetDocumentList(applicationId, (result) => {
            setDocumentList(result || []);
          });
        }
        // Show delete success modal
        setShowDeleteSuccessModal(true);
      });
    }
  };

  // Load document list on component mount
  useEffect(() => {
    if (doGetDocumentList && (application.id || application.applicationId)) {
      doGetDocumentList(
        application.id || application.applicationId,
        (result) => {
          setDocumentList(result || []);
        }
      );
    }
  }, [application.id, application.applicationId, doGetDocumentList]);

  // Load staff list on component mount
  useEffect(() => {
    dispatch(doGetStaffList());
  }, [dispatch]);

  // Load application notes on component mount
  useEffect(() => {
    console.log(
      "ApplicationDetails - doGetApplicationNotes useEffect triggered",
      {
        doGetApplicationNotes: !!doGetApplicationNotes,
        applicationId: application.id || application.applicationId,
        application,
      }
    );

    if (
      doGetApplicationNotes &&
      (application.id || application.applicationId)
    ) {
      console.log(
        "ApplicationDetails - Calling doGetApplicationNotes with ID:",
        application.id || application.applicationId
      );
      setApplicationNotesLoading(true);
      doGetApplicationNotes(
        application.id || application.applicationId,
        (result) => {
          console.log(
            "ApplicationDetails - doGetApplicationNotes callback result:",
            result
          );
          setApplicationNotes(result?.data || result || []);
          setApplicationNotesLoading(false);
        }
      );
    } else {
      console.log("ApplicationDetails - Skipping doGetApplicationNotes", {
        hasFunction: !!doGetApplicationNotes,
        hasId: !!(application.id || application.applicationId),
      });
    }
  }, [application.id, application.applicationId, doGetApplicationNotes]);

  // Load paysafe details when funding tab is selected
  useEffect(() => {
    if (
      activeTab === "fundingDetails" &&
      (application.id || application.applicationId)
    ) {
      setPaysafeLoading(true);
      dispatch(
        getPaysafeDetails(
          application.id || application.applicationId,
          (response) => {
            setPaysafeLoading(false);
            if (response && response.data) {
              setPaysafeDetails(
                Array.isArray(response.data) ? response.data : []
              );
            } else {
              setPaysafeDetails([]);
            }
          }
        )
      );
    }
  }, [activeTab, application.id, application.applicationId, dispatch]);

  const productId =
    application.productId ||
    getProductInfoFromCurrentDoximProducts(
      application?.doximProducts || applicationProperty?.doximProducts,
      productList
    )?.productId;
  const productIdNumber = applicationProperty?.doximProducts
    ? applicationProperty.doximProducts.filter(
        (product) => product.type !== "debitCard"
      )[0]?.id
    : null;
  const productType = getProductInfoFromCurrentDoximProducts(
    application?.doximProducts || applicationProperty?.doximProducts,
    productList
  )?.productType;
  const productName =
    application.productName ||
    getProductInfoFromCurrentDoximProducts(
      application.doximProducts,
      productList
    )?.productName;

  return (
    <div
      style={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        padding: "20px",
      }}
    >
      {/* File Viewer Modal */}
      {fileViewerModalOpen && viewingFile && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.8)", zIndex: 1050 }}
          onClick={closeFileViewer}
        >
          <div
            className="modal-dialog modal-xl"
            style={{ maxWidth: "90%", height: "90vh", margin: "2.5vh auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-content"
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                className="modal-header"
                style={{ borderBottom: "1px solid #dee2e6" }}
              >
                <h5 className="modal-title">
                  <i className="fas fa-file-alt mr-2"></i>
                  {viewingFile.documentName}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeFileViewer}
                ></button>
              </div>
              <div
                className="modal-body p-0"
                style={{
                  flex: 1,
                  overflow: "auto",
                  backgroundColor: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {fileViewerUrl && (
                  <>
                    {viewingFile.documentName
                      ?.toLowerCase()
                      .endsWith(".pdf") ? (
                      <iframe
                        src={`${fileViewerUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                        }}
                        title="PDF Viewer"
                      />
                    ) : ["jpg", "jpeg", "png", "gif", "bmp"].includes(
                        viewingFile.documentName
                          ?.split(".")
                          .pop()
                          ?.toLowerCase()
                      ) ? (
                      <img
                        src={fileViewerUrl}
                        alt={viewingFile.documentName}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          console.error("Image failed to load:", e);
                        }}
                      />
                    ) : ["mp4", "avi", "mov", "wmv"].includes(
                        viewingFile.documentName
                          ?.split(".")
                          .pop()
                          ?.toLowerCase()
                      ) ? (
                      <video
                        controls
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                        }}
                      >
                        <source src={fileViewerUrl} />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <div className="text-center p-5">
                        <i
                          className="fas fa-file-alt"
                          style={{
                            fontSize: "4rem",
                            color: "#6c757d",
                            marginBottom: "1rem",
                          }}
                        ></i>
                        <p className="text-muted">
                          Preview not available for this file type.
                          <br />
                          Please download the file to view it.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div
                className="modal-footer"
                style={{
                  borderTop: "1px solid #dee2e6",
                  justifyContent: "left",
                  gap: "0.5rem",
                  padding: "1rem",
                }}
              >
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handlePrintFile}
                  style={{ borderRadius: "0.5rem" }}
                >
                  <span className="fas fa-print"></span>
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => handleFileDownload(viewingFile)}
                  style={{ borderRadius: "0.5rem" }}
                >
                  <span className="fas fa-file-download"></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mini Breadcrumb */}
      <div style={{ marginBottom: "20px" }}>
        <div className="[ breadcrumb-container ]">
          <ol className="[ breadcrumb ]">
            <li className="[ breadcrumb-icon ]">
              <a
                role="button"
                className="link"
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault();
                  handleBreadcrumbBackToApplications();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleBreadcrumbBackToApplications();
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                Applications
              </a>
            </li>
            <li className="[ breadcrumb-icon ] [ active ]" aria-current="page">
              {productName}
            </li>
          </ol>
        </div>
      </div>

      {/* Header */}
      <div style={{ marginBottom: "30px" }}>
        <h2
          style={{
            color: "#333",
            fontSize: "1.8rem",
            fontWeight: "bold",
            marginBottom: "5px",
          }}
        >
          {productName?.toUpperCase() || "APPLICATION"} -
          <span
            className={
              application.inProgress
                ? "text-primary"
                : application.inReview
                ? "text-warning"
                : "text-success"
            }
            style={{ marginLeft: "10px" }}
          >
            {application.inProgress
              ? finInfo?.config?.status?.inProgress || "IN PROGRESS"
              : application.inReview
              ? finInfo?.config?.status?.inReview || "IN REVIEW"
              : finInfo?.config?.status?.complete || "COMPLETED"}
          </span>
        </h2>
      </div>

      <div className="row">
        {/* Left Column - Product Details */}
        <div className="col-8">
          {/* Tabs */}
          <div style={{ marginBottom: "20px" }}>
            <ul
              className="nav nav-tabs"
              style={{ borderBottom: "2px solid #dee2e6" }}
            >
              <li className="nav-item">
                <a
                  className={`nav-link ${
                    activeTab === "productDetails" ? "active" : ""
                  }`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab("productDetails");
                  }}
                  style={{
                    color: activeTab === "productDetails" ? "#333" : "#6c757d",
                    fontWeight:
                      activeTab === "productDetails" ? "600" : "normal",
                    borderBottom:
                      activeTab === "productDetails"
                        ? "3px solid #007bff"
                        : "none",
                    cursor: "pointer",
                  }}
                >
                  Product Details
                </a>
              </li>
              <li className="nav-item">
                <a
                  className={`nav-link ${
                    activeTab === "jointDetails" ? "active" : ""
                  }`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab("jointDetails");
                  }}
                  style={{
                    color: activeTab === "jointDetails" ? "#333" : "#6c757d",
                    fontWeight: activeTab === "jointDetails" ? "600" : "normal",
                    borderBottom:
                      activeTab === "jointDetails"
                        ? "3px solid #007bff"
                        : "none",
                    cursor: "pointer",
                  }}
                >
                  Joint Details
                </a>
              </li>
              <li className="nav-item">
                <a
                  className={`nav-link ${
                    activeTab === "fundingDetails" ? "active" : ""
                  }`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab("fundingDetails");
                  }}
                  style={{
                    color: activeTab === "fundingDetails" ? "#333" : "#6c757d",
                    fontWeight:
                      activeTab === "fundingDetails" ? "600" : "normal",
                    borderBottom:
                      activeTab === "fundingDetails"
                        ? "3px solid #007bff"
                        : "none",
                    cursor: "pointer",
                  }}
                >
                  Funding Details
                </a>
              </li>
            </ul>
          </div>

          {/* Product Details Table */}
          {activeTab === "productDetails" && (
            <div
              style={{
                backgroundColor: "white",
                border: "1px solid #dee2e6",
                borderRadius: "8px",
                marginBottom: "30px",
              }}
            >
              <table className="table table-borderless" style={{ margin: 0 }}>
                <tbody>
                  <tr style={{ borderBottom: "1px solid #f1f3f4" }}>
                    <td
                      style={{
                        fontWeight: "600",
                        color: "#333",
                        padding: "15px 20px",
                      }}
                    >
                      Product name:
                    </td>
                    <td style={{ color: "#666", padding: "15px 20px" }}>
                      {productName}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid #f1f3f4" }}>
                    <td
                      style={{
                        fontWeight: "600",
                        color: "#333",
                        padding: "15px 20px",
                      }}
                    >
                      Create date:
                    </td>
                    <td style={{ color: "#666", padding: "15px 20px" }}>
                      {application.created
                        ? new Date(
                            application.created *
                              (application.created.toString().length === 10
                                ? 1000
                                : 1)
                          ).toLocaleDateString()
                        : applicationProperty?.created
                        ? new Date(
                            applicationProperty.created *
                              (applicationProperty.created.toString().length ===
                              10
                                ? 1000
                                : 1)
                          ).toLocaleDateString()
                        : ""}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid #f1f3f4" }}>
                    <td
                      style={{
                        fontWeight: "600",
                        color: "#333",
                        padding: "15px 20px",
                      }}
                    >
                      Submit date:
                    </td>
                    <td style={{ color: "#666", padding: "15px 20px" }}>
                      {application.appliedAt
                        ? new Date(
                            application.appliedAt *
                              (application.appliedAt.toString().length === 10
                                ? 1000
                                : 1)
                          ).toLocaleDateString()
                        : ""}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid #f1f3f4" }}>
                    <td
                      style={{
                        fontWeight: "600",
                        color: "#333",
                        padding: "15px 20px",
                      }}
                    >
                      Product ID:
                    </td>
                    <td style={{ color: "#666", padding: "15px 20px" }}>
                      {productId}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid #f1f3f4" }}>
                    <td
                      style={{
                        fontWeight: "600",
                        color: "#333",
                        padding: "15px 20px",
                      }}
                    >
                      Application ID:
                    </td>
                    <td style={{ color: "#666", padding: "15px 20px" }}>
                      {application.id || application.applicationId}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid #f1f3f4" }}>
                    <td
                      style={{
                        fontWeight: "600",
                        color: "#333",
                        padding: "15px 20px",
                      }}
                    >
                      Member ID:
                    </td>
                    <td style={{ color: "#666", padding: "15px 20px" }}>
                      {sessionStorage.getItem(S_APPLICANT_MEMBER_NO)}
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        fontWeight: "600",
                        color: "#333",
                        padding: "15px 20px",
                      }}
                    >
                      Applicant ID:
                    </td>
                    <td style={{ color: "#666", padding: "15px 20px" }}>
                      {application.applicantId ||
                        sessionStorage.getItem(S_APPLICANT_ID)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Joint Details Content */}
          {activeTab === "jointDetails" && (
            <div
              style={{
                backgroundColor: "white",
                border: "1px solid #dee2e6",
                borderRadius: "8px",
                marginBottom: "30px",
                padding: "20px",
              }}
            >
              {Array.isArray(applicationProperty?.slots) &&
              applicationProperty.slots.length > 1 ? (
                applicationProperty.slots
                  .filter((app) => app.type === "co")
                  ?.map((joint, idx) => (
                    <div
                      key={idx}
                      style={{
                        marginBottom: "15px",
                        paddingBottom: "15px",
                        borderBottom:
                          idx <
                          applicationProperty.slots.filter(
                            (app) => app.type === "co"
                          ).length -
                            1
                            ? "1px solid #dee2e6"
                            : "none",
                      }}
                    >
                      <div
                        style={{
                          marginBottom: "12px",
                          fontWeight: "600",
                          fontSize: "1rem",
                          color: "#333",
                        }}
                      >
                        Co-Applicant {idx + 1}
                      </div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Email:</strong>{" "}
                        {getNestedValue(joint, "extra.email")}
                      </div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Phone:</strong>{" "}
                        {joint.extra?.phoneNumber || "—"}
                      </div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>In session:</strong>{" "}
                        {joint.extra?.inSession ? "Yes" : "No"}
                      </div>
                    </div>
                  ))
              ) : (
                <p style={{ margin: 0, fontStyle: "italic", color: "#666" }}>
                  No joint applicants
                </p>
              )}
            </div>
          )}

          {/* Funding Details Content */}
          {activeTab === "fundingDetails" && (
            <div
              style={{
                backgroundColor: "white",
                border: "1px solid #dee2e6",
                borderRadius: "8px",
                marginBottom: "30px",
                padding: "20px",
              }}
            >
              {paysafeLoading ? (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p style={{ marginTop: "10px", color: "#666" }}>
                    Loading paysafe transactions...
                  </p>
                </div>
              ) : paysafeDetails.length > 0 ? (
                paysafeDetails.map((transaction, idx) => {
                  const isSuccess =
                    transaction.statusCode >= 200 &&
                    transaction.statusCode < 300;

                  return (
                    <div
                      key={transaction.id || idx}
                      style={{
                        marginBottom: "15px",
                        paddingBottom: "15px",
                        borderBottom:
                          idx < paysafeDetails.length - 1
                            ? "1px solid #dee2e6"
                            : "none",
                      }}
                    >
                      <div
                        style={{
                          marginBottom: "12px",
                          fontWeight: "600",
                          fontSize: "1rem",
                          color: "#333",
                        }}
                      >
                        Transaction {idx + 1}
                      </div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Transaction ref ID:</strong>{" "}
                        {transaction.id || "—"}
                      </div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Transaction create date:</strong>{" "}
                        {transaction.createdAt
                          ? formatDate(transaction.createdAt)
                          : "—"}
                      </div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Funding method:</strong>{" "}
                        {transaction.paymentType || "—"}
                      </div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Funding amount:</strong>{" "}
                        {transaction.amount
                          ? `$${parseFloat(transaction.amount).toFixed(2)}`
                          : "—"}
                      </div>

                      {transaction.errorMessage && (
                        <div style={{ marginBottom: "8px" }}>
                          <strong>Error:</strong> {transaction.errorMessage}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <p style={{ margin: 0, fontStyle: "italic", color: "#666" }}>
                  No paysafe transactions found for this application.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Right Column - Application Activity Timeline */}
        <div className="col-4" style={{ marginBottom: "30px" }}>
          <div
            style={{
              backgroundColor: "#5bc0de",
              color: "white",
              padding: "10px 15px",
              borderRadius: "8px 8px 0 0",
              fontSize: "0.9rem",
              fontWeight: "600",
              textAlign: "center",
            }}
          >
            Submission Type:{" "}
            {applicationProperty?.staffId ? "WEB" : "SELF-SERVE"}
          </div>

          <div
            style={{
              backgroundColor: "white",
              border: "1px solid #dee2e6",
              borderTop: "none",
              borderRadius: "0 0 8px 8px",
              padding: "20px",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0" style={{ fontWeight: "600", color: "#333" }}>
                Application Activity Timeline
              </h6>
              <button
                className="btn btn-link p-0"
                style={{
                  color: "#007bff",
                  fontSize: "0.8rem",
                  textDecoration: "none",
                }}
              >
                See Details <i className="fas fa-chevron-right ms-1"></i>
              </button>
            </div>

            {/* Timeline Items */}
            <div
              style={{
                position: "relative",
                maxHeight: "320px",
                overflowY: "auto",
              }}
            >
              {/* Timeline Line */}
              <div
                style={{
                  position: "absolute",
                  left: "15px",
                  top: "30px",
                  bottom: "0",
                  width: "2px",
                  backgroundColor: "#dee2e6",
                }}
              ></div>
              {/* Submit Event */}
              {application?.appliedAt && (
                <div className="d-flex mb-4">
                  <div
                    style={{
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      backgroundColor: "#28a745",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "15px",
                      zIndex: 1,
                      position: "relative",
                    }}
                  >
                    <i
                      className="fas fa-user"
                      style={{ color: "white", fontSize: "0.8rem" }}
                    ></i>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <h6
                        style={{
                          margin: 0,
                          fontWeight: "600",
                          color: "#333",
                          fontSize: "0.9rem",
                        }}
                      >
                        Submit
                      </h6>
                      <small style={{ color: "#6c757d", fontSize: "0.75rem" }}>
                        {getRelativeTime(application?.created)}
                      </small>
                    </div>
                    <div
                      style={{
                        fontSize: "0.8rem",
                        color: "#666",
                        lineHeight: "1.4",
                      }}
                    >
                      <div>
                        <strong>WHO:</strong> {applicant?.member?.name}{" "}
                        {applicant?.member?.surname}
                      </div>
                      <div>
                        <strong>WHAT:</strong> Application Submission
                      </div>
                      <div>
                        <strong>WHERE:</strong>{" "}
                        {applicationProperty?.staffId ? "Web" : "Self-serve"}
                      </div>
                      <div>
                        <strong>WHY:</strong> —
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Override Events - Create timeline entries and sort chronologically */}
              {!applicationNotesLoading &&
                (() => {
                  // Create a flattened array of timeline entries
                  const timelineEntries = [];

                  applicationNotes
                    .filter(
                      (note) =>
                        note.reviewCode === "OVERRIDE" ||
                        note.reviewCode === "UPHOLD" ||
                        note.reviewCode === "ESCALATE" ||
                        note.noteType === "SUBMIT"
                    )
                    .forEach((note) => {
                      // Add assignDate entry if it exists
                      if (
                        (note.reviewCode === "OVERRIDE" ||
                          note.reviewCode === "UPHOLD") &&
                        note.assignDate
                      ) {
                        timelineEntries.push({
                          type: "assign",
                          note: note,
                          timestamp: note.assignDate,
                          id: note.id + "_assign",
                        });
                      }

                      if (note.noteType === "SUBMIT") {
                        timelineEntries.push({
                          type: "submit",
                          note: note,
                          timestamp: note.created,
                          id: note.id + "_submit",
                        });
                      }

                      // Add reviewDate entry
                      if (note.reviewDate) {
                        timelineEntries.push({
                          type: "review",
                          note: note,
                          timestamp: note.reviewDate,
                          id: note.id + "_review",
                        });
                      }
                    });

                  // Sort all entries by timestamp
                  timelineEntries.sort((a, b) => a.timestamp - b.timestamp);

                  return timelineEntries.map((entry, index) => (
                    <div
                      key={entry.id}
                      className={`d-flex ${
                        index < timelineEntries.length - 1 ? "mb-4" : ""
                      }`}
                    >
                      {entry.type === "assign" ? (
                        <>
                          <div
                            style={{
                              width: "30px",
                              height: "30px",
                              borderRadius: "50%",
                              backgroundColor: (() => {
                                const staff = staffList.find(
                                  (s) => s.id === entry.note.assignBy
                                );
                                return staff?.role === "Branch Employee"
                                  ? "#17a2b8"
                                  : "#ffc107";
                              })(),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginRight: "15px",
                              zIndex: 1,
                              position: "relative",
                            }}
                          >
                            <i
                              className="fas fa-edit"
                              style={{ color: "white", fontSize: "0.8rem" }}
                            ></i>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <h6
                                style={{
                                  margin: 0,
                                  fontWeight: "600",
                                  color: "#333",
                                  fontSize: "0.9rem",
                                }}
                              >
                                Modification
                              </h6>
                              <small
                                style={{
                                  color: "#6c757d",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {getRelativeTime(entry.note.assignDate)}
                              </small>
                            </div>
                            <div
                              style={{
                                fontSize: "0.8rem",
                                color: "#666",
                                lineHeight: "1.4",
                              }}
                            >
                              <div>
                                <strong>WHO:</strong>{" "}
                                {getColleagueDisplayName(entry.note.assignBy) ||
                                  "—"}
                              </div>
                              <div>
                                <strong>WHAT:</strong> Modification
                              </div>
                              <div>
                                <strong>WHERE:</strong> Web
                              </div>
                              <div>
                                <strong>WHY:</strong>{" "}
                                {entry.note.assignNotes || "—"}
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div
                            style={{
                              width: "30px",
                              height: "30px",
                              borderRadius: "50%",
                              backgroundColor: (() => {
                                const staff = staffList.find(
                                  (s) => s.id === entry.note.reviewBy
                                );
                                return staff?.role === "Branch Employee"
                                  ? "#17a2b8"
                                  : "#ffc107";
                              })(),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginRight: "15px",
                              zIndex: 1,
                              position: "relative",
                            }}
                          >
                            <i
                              className="fas fa-edit"
                              style={{ color: "white", fontSize: "0.8rem" }}
                            ></i>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <h6
                                style={{
                                  margin: 0,
                                  fontWeight: "600",
                                  color: "#333",
                                  fontSize: "0.9rem",
                                }}
                              >
                                {entry.type === "submit"
                                  ? "Re-Submit"
                                  : "Modification"}
                              </h6>
                              <small
                                style={{
                                  color: "#6c757d",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {getRelativeTime(
                                  entry.note.reviewDate || entry.note.created
                                )}
                              </small>
                            </div>
                            <div
                              style={{
                                fontSize: "0.8rem",
                                color: "#666",
                                lineHeight: "1.4",
                              }}
                            >
                              <div>
                                <strong>WHO:</strong>{" "}
                                {entry.note.reviewCode === "ESCALATE"
                                  ? getColleagueDisplayName(entry.note.assignBy)
                                  : entry.note.reviewBy
                                  ? getColleagueDisplayName(entry.note.reviewBy)
                                  : "—"}
                              </div>
                              <div>
                                <strong>WHAT:</strong> Modification
                              </div>
                              <div>
                                <strong>WHERE:</strong> Web
                              </div>
                              <div>
                                <strong>WHY:</strong>{" "}
                                {entry.note.reviewNotes ||
                                  entry.note.assignNotes ||
                                  "—"}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ));
                })()}
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Attached Documents */}
        <div className="col-12">
          <div
            style={{
              backgroundColor: "white",
              border: "1px solid #dee2e6",
              borderRadius: "8px",
              padding: "20px",
              marginBottom: "20px",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center">
                <i
                  className="fas fa-folder-open me-2"
                  style={{ color: "#333" }}
                ></i>
                <h6
                  className="mb-0"
                  style={{ fontWeight: "600", color: "#333" }}
                >
                  Attached Documents
                </h6>
              </div>
              <button
                className="btn btn-outline-primary btn-sm"
                style={{ fontSize: "0.8rem" }}
                onClick={() => fileInputRef.current?.click()}
                disabled={!(application.applicationId || application.id)}
              >
                + Upload New
              </button>
              {/* Hidden file input for direct file selection */}
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4,.avi,.mov"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file && doAddDocument) {
                    const applicationId =
                      application.id || application.applicationId;

                    // Create FormData following DocumentsContainer.js pattern
                    const formData = new FormData();
                    formData.append("System", "product");
                    formData.append("ApplicationId", applicationId);
                    formData.append("ProductId", productIdNumber);
                    formData.append("ProductType", productType);
                    formData.append("DocumentSource", file);

                    // Call doAddDocument with applicationId, productIdNumber, formData, and callback
                    doAddDocument(
                      applicationId,
                      productIdNumber,
                      formData,
                      (res) => {
                        console.log(res);
                        if (res.status === HTTP_STATUS.OK) {
                          // Re-query document list after successful upload
                          if (doGetDocumentList) {
                            doGetDocumentList(applicationId, (result) => {
                              setDocumentList(result || []);
                            });
                          }
                          // Show success modal
                          setShowSuccessModal(true);
                        }
                      }
                    );

                    // Clear the input value so the same file can be selected again if needed
                    e.target.value = "";
                  }
                }}
              />
            </div>

            {documentList.length > 0 ? (
              <ul className="branch-widget--uploaded-files__list">
                {documentList.map((doc) => {
                  const fileIcon = getFileIconFromName(doc.documentName);
                  const fileType = getFileTypeFromName(doc.documentName);
                  return (
                    <li
                      key={doc.documentId}
                      className="branch-widget--uploaded-files__list-item"
                    >
                      <Card className="branch-widget--uploaded-files__card">
                        <Card.Body className="branch-widget--uploaded-files__body">
                          <div className="branch-widget--uploaded-files__row">
                            {/* Avatar/file type icon */}
                            <div className="branch-widget--uploaded-files__avatar">
                              <i
                                className={`branch-widget--uploaded-files__icon ${fileIcon.icon}`}
                                aria-hidden="true"
                              />
                              <div className="branch-widget--uploaded-files__meta">
                                {fileType}
                              </div>
                            </div>
                            {/* Main content */}
                            <div className="branch-widget--uploaded-files__content">
                              <div className="branch-widget--uploaded-files__header">
                                <h3
                                  className="branch-widget--uploaded-files__title"
                                  title={doc.documentName}
                                >
                                  {doc.documentName}
                                </h3>
                                {/* CHRIS: Hiding document delete button as was recommended */}
                                {/* <button
                                type="button"
                                className="branch-widget--uploaded-files__action"
                                aria-label={`Delete file ${doc.documentName}`}
                                onClick={() => handleDeleteDocument(doc)}
                              >
                                <i
                                  className="fas fa-trash"
                                  aria-hidden="true"
                                ></i>
                              </button> */}
                                <button
                                  type="button"
                                  className="branch-widget--uploaded-files__action"
                                  aria-label={`View file ${doc.documentName}`}
                                  onClick={() => handleDownloadDocument(doc)}
                                >
                                  <i
                                    className="fas fa-search-plus"
                                    aria-hidden="true"
                                  ></i>
                                </button>
                              </div>
                              <div className="branch-widget--uploaded-files__meta">
                                <strong>Uploaded on:</strong>{" "}
                                {formatFileDateTime(doc.createdDate)}
                              </div>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e9ecef",
                  borderRadius: "4px",
                  padding: "20px",
                  textAlign: "center",
                }}
              >
                <p style={{ margin: 0, fontSize: "0.9rem", color: "#999" }}>
                  No documents attached
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Application Details Section */}
        <div className="col-12">
          <div
            style={{
              backgroundColor: "white",
              border: "1px solid #dee2e6",
              borderRadius: "8px",
              padding: "20px",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center">
                <i
                  className={
                    "fas fa-file-alt me-2 " +
                    (application.inProgress
                      ? "text-primary"
                      : application.inReview
                      ? "text-warning"
                      : "text-success")
                  }
                ></i>
                <h6
                  className={
                    application.inProgress
                      ? "text-primary"
                      : application.inReview
                      ? "text-warning"
                      : "text-success"
                  }
                  style={{
                    fontWeight: "600",
                    paddingLeft: ".5em",
                    paddingTop: ".5em",
                  }}
                >
                  Application Details -{" "}
                  {application.inProgress
                    ? finInfo?.config?.status?.inProgress || "In Progress"
                    : application.inReview
                    ? finInfo?.config?.status?.inReview || "In Review"
                    : finInfo?.config?.status?.complete || "Completed"}
                </h6>
              </div>
              <button
                className="btn btn-link p-0"
                style={{
                  color: "#dc3545",
                  fontSize: "0.9rem",
                  textDecoration: "none",
                }}
                onClick={() => setShowDetailedView(!showDetailedView)}
              >
                {showDetailedView ? "Hide" : "View"} "Application Details"{" "}
                <i
                  className={`fas fa-chevron-${
                    showDetailedView ? "down" : "right"
                  } ms-1`}
                ></i>
              </button>
            </div>

            {application.inReview && (
              <div
                style={{
                  color: "#666",
                  fontSize: "0.95rem",
                  lineHeight: "1.6",
                  marginBottom: "15px",
                }}
              >
                <p style={{ marginBottom: "10px" }}>
                  <strong>
                    Review and adjudicate the failed reason codes.
                  </strong>
                </p>
                <p style={{ margin: 0 }}>
                  Indicate whether to uphold the failure(s), override the
                  failure(s) or escalate them for another individual to review.
                </p>
              </div>
            )}

            {showDetailedView && (
              <div className="row mt-3">
                {/* Left Column - Items Requiring Review */}
                <div className="col-6">
                  <div
                    style={{
                      padding: "15px",
                      marginBottom: "20px",
                    }}
                  >
                    <h6
                      style={{
                        fontWeight: "600",
                        color: "#333",
                        marginBottom: "15px",
                      }}
                    >
                      ITEMS REQUIRING REVIEW
                    </h6>

                    {/* Loading state */}
                    {applicationNotesLoading && (
                      <div style={{ textAlign: "center", padding: "20px" }}>
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}

                    {/* No items message */}
                    {!applicationNotesLoading && applicationNotes.length === 0 && (
                      <div
                        style={{
                          backgroundColor: "white",
                          border: "1px solid #dee2e6",
                          borderRadius: "4px",
                          padding: "20px",
                          textAlign: "center",
                          color: "#666",
                        }}
                      >
                        No items requiring review at this time.
                      </div>
                    )}

                    {/* Dynamic Review Items */}
                    {!applicationNotesLoading &&
                      applicationNotes
                        .filter((note) => note.noteType !== "SUBMIT")
                        .map((note, index) => (
                          <div
                            key={note.id || index}
                            style={{
                              backgroundColor: "white",
                              border: "1px solid #dee2e6",
                              borderRadius: "4px",
                              marginBottom:
                                index < applicationNotes.length - 1
                                  ? "15px"
                                  : "0",
                            }}
                          >
                            <div
                              className="d-flex justify-content-between align-items-start"
                              style={{
                                backgroundColor: note.reviewCode
                                  ? "#d3ffcf"
                                  : "#fff5f5",
                                padding: "8px",
                              }}
                            >
                              <div>
                                <span
                                  style={{
                                    fontSize: "0.75rem",
                                    fontWeight: "600",
                                    color: note.reviewCode ? "blue" : "red",
                                  }}
                                >
                                  STATUS: {note.reviewCode || "Needs review"}
                                </span>
                              </div>
                              <button
                                className="btn btn-link p-0"
                                style={{
                                  color: "#dc3545",
                                  fontSize: "0.75rem",
                                  textDecoration: "none",
                                }}
                                onClick={() =>
                                  toggleNoteExpansion(note.id || index)
                                }
                              >
                                {expandedNotes[note.id || index]
                                  ? "Show less"
                                  : "View"}{" "}
                                <i
                                  className={`fas fa-chevron-${
                                    expandedNotes[note.id || index]
                                      ? "up"
                                      : "down"
                                  } ms-1`}
                                ></i>
                              </button>
                            </div>
                            <div
                              className="d-flex justify-content-between align-items-start"
                              style={{
                                backgroundColor: note.reviewCode
                                  ? "#d3ffcf"
                                  : "#fff5f5",
                              }}
                            >
                              {note.code && (
                                <div
                                  style={{
                                    padding: "2px 8px",
                                    fontSize: "0.75rem",
                                    fontWeight: "600",
                                  }}
                                >
                                  Error Code: {note.code}
                                </div>
                              )}
                            </div>
                            <div
                              className="d-flex justify-content-between align-items-start"
                              style={{
                                backgroundColor: note.reviewCode
                                  ? "#d3ffcf"
                                  : "#fff5f5",
                              }}
                            >
                              {note.summary && (
                                <div
                                  style={{
                                    padding: "2px 8px",
                                    fontSize: "0.75rem",
                                    fontWeight: "600",
                                  }}
                                >
                                  {note.summary}
                                </div>
                              )}
                            </div>
                            {expandedNotes[note.id || index] && (
                              <div style={{ padding: "15px" }}>
                                <div
                                  style={{
                                    fontSize: "0.85rem",
                                    color: "#666",
                                    marginTop: "10px",
                                  }}
                                >
                                  {note.details && note.details !== "true" && (
                                    <div style={{ marginBottom: "10px" }}>
                                      {note.details}
                                    </div>
                                  )}

                                  <div className="[ divider ]" />
                                  <div
                                    style={{
                                      marginBottom: "10px",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    I have reviewed this and I find it
                                    appropriate to:
                                  </div>
                                  <div className="d-flex mb-2 justify-content-center">
                                    <button
                                      className={`btn btn-sm mx-2 ${
                                        note.reviewCode === "UPHOLD" ||
                                        note.reviewCode === "OVERRIDE"
                                          ? note.reviewCode.toLowerCase() ===
                                            "uphold"
                                            ? "btn-dark"
                                            : "btn-light"
                                          : reviewSelections[note.id] ===
                                            "uphold"
                                          ? "btn-primary"
                                          : "btn-outline-secondary"
                                      }`}
                                      onClick={() =>
                                        handleReviewSelection(note.id, "uphold")
                                      }
                                      disabled={
                                        note.reviewCode === "UPHOLD" ||
                                        note.reviewCode === "OVERRIDE"
                                      }
                                      style={
                                        note.reviewCode === "UPHOLD" ||
                                        note.reviewCode === "OVERRIDE"
                                          ? {
                                              cursor: "not-allowed",
                                              color:
                                                note.reviewCode.toLowerCase() ===
                                                "uphold"
                                                  ? undefined
                                                  : "#000",
                                              backgroundColor:
                                                note.reviewCode.toLowerCase() ===
                                                "uphold"
                                                  ? undefined
                                                  : "#fff",
                                              borderColor:
                                                note.reviewCode.toLowerCase() ===
                                                "uphold"
                                                  ? undefined
                                                  : "#dee2e6",
                                            }
                                          : {}
                                      }
                                    >
                                      Uphold
                                    </button>
                                    <button
                                      className={`btn btn-sm mx-2 ${
                                        note.reviewCode === "UPHOLD" ||
                                        note.reviewCode === "OVERRIDE"
                                          ? note.reviewCode.toLowerCase() ===
                                            "override"
                                            ? "btn-dark"
                                            : "btn-light"
                                          : reviewSelections[note.id] ===
                                            "override"
                                          ? "btn-primary"
                                          : "btn-outline-secondary"
                                      }`}
                                      onClick={() =>
                                        handleReviewSelection(
                                          note.id,
                                          "override"
                                        )
                                      }
                                      disabled={
                                        note.reviewCode === "UPHOLD" ||
                                        note.reviewCode === "OVERRIDE"
                                      }
                                      style={
                                        note.reviewCode === "UPHOLD" ||
                                        note.reviewCode === "OVERRIDE"
                                          ? {
                                              cursor: "not-allowed",
                                              color:
                                                note.reviewCode.toLowerCase() ===
                                                "override"
                                                  ? undefined
                                                  : "#000",
                                              backgroundColor:
                                                note.reviewCode.toLowerCase() ===
                                                "override"
                                                  ? undefined
                                                  : "#fff",
                                              borderColor:
                                                note.reviewCode.toLowerCase() ===
                                                "override"
                                                  ? undefined
                                                  : "#dee2e6",
                                            }
                                          : {}
                                      }
                                    >
                                      Override
                                    </button>
                                    <button
                                      className={`btn btn-sm mx-2 ${
                                        note.reviewCode
                                          ? note.reviewCode.toLowerCase() ===
                                            "escalate"
                                            ? "btn-dark"
                                            : "btn-light"
                                          : reviewSelections[note.id] ===
                                            "escalate"
                                          ? "btn-primary"
                                          : "btn-outline-secondary"
                                      }`}
                                      onClick={() =>
                                        handleReviewSelection(
                                          note.id,
                                          "escalate"
                                        )
                                      }
                                      disabled={!!note.reviewCode}
                                      style={
                                        note.reviewCode
                                          ? {
                                              cursor: "not-allowed",
                                              color:
                                                note.reviewCode.toLowerCase() ===
                                                "escalate"
                                                  ? undefined
                                                  : "#000",
                                              backgroundColor:
                                                note.reviewCode.toLowerCase() ===
                                                "escalate"
                                                  ? undefined
                                                  : "#fff",
                                              borderColor:
                                                note.reviewCode.toLowerCase() ===
                                                "escalate"
                                                  ? undefined
                                                  : "#dee2e6",
                                            }
                                          : {}
                                      }
                                    >
                                      Escalate
                                    </button>
                                  </div>

                                  {/* Escalation Options - Show when Escalate is selected */}
                                  {!note.reviewCode &&
                                    reviewSelections[note.id] ===
                                      "escalate" && (
                                      <div
                                        style={{
                                          marginTop: "15px",
                                          padding: "15px",
                                          backgroundColor: "#f8f9fa",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        <div style={{ marginBottom: "15px" }}>
                                          <p
                                            style={{
                                              fontSize: "0.85rem",
                                              marginBottom: "10px",
                                              fontWeight: "600",
                                            }}
                                          >
                                            Select how you would like a
                                            colleague to review this
                                            application.
                                          </p>
                                          <div className="d-flex gap-2">
                                            <button
                                              className={`btn btn-sm mx-2 ${
                                                escalationMethod[note.id] ===
                                                "task"
                                                  ? "btn-dark"
                                                  : "btn-outline-secondary"
                                              }`}
                                              onClick={() =>
                                                setEscalationMethod((prev) => ({
                                                  ...prev,
                                                  [note.id]: "task",
                                                }))
                                              }
                                              style={{ flex: 1 }}
                                            >
                                              Assign as a task
                                            </button>
                                            <button
                                              className={`btn btn-sm mx-2 ${
                                                escalationMethod[note.id] ===
                                                "in-person"
                                                  ? "btn-dark"
                                                  : "btn-outline-secondary"
                                              }`}
                                              onClick={() =>
                                                setEscalationMethod((prev) => ({
                                                  ...prev,
                                                  [note.id]: "in-person",
                                                }))
                                              }
                                              style={{ flex: 1 }}
                                            >
                                              In person - now
                                            </button>
                                          </div>
                                        </div>

                                        <div style={{ marginBottom: "15px" }}>
                                          <p
                                            style={{
                                              fontSize: "0.85rem",
                                              marginBottom: "5px",
                                            }}
                                          >
                                            Select the colleague(s) you want to
                                            assign to review this application.
                                          </p>
                                          <p
                                            style={{
                                              fontSize: "0.75rem",
                                              color: "#666",
                                              marginBottom: "10px",
                                            }}
                                          >
                                            <strong>NOTE:</strong> Only
                                            colleagues with the appropriate
                                            privileges to adjudicate this
                                            application are available to invite.
                                          </p>
                                          <p
                                            style={{
                                              fontSize: "0.8rem",
                                              marginBottom: "10px",
                                            }}
                                          >
                                            This application requires 1
                                            qualified colleague to complete
                                            adjudication.
                                          </p>
                                          <select
                                            name="assignTo"
                                            className="form-select form-select-sm"
                                            value={
                                              (selectedColleague &&
                                                selectedColleague[note.id]) ??
                                              ""
                                            }
                                            onChange={(e) => {
                                              if (e && e.target) {
                                                const selectedValue =
                                                  e.target.value;
                                                setSelectedColleague(
                                                  (prev) => ({
                                                    ...prev,
                                                    [note.id]: selectedValue,
                                                  })
                                                );
                                              }
                                            }}
                                            style={{ fontSize: "0.85rem" }}
                                            disabled={!!note.reviewCode}
                                          >
                                            <option value="">
                                              Select a colleague...
                                            </option>
                                            {staffList
                                              .filter(
                                                (staff) =>
                                                  staff.id !==
                                                  sessionStorage.getItem(
                                                    S_STAFF_ID
                                                  )
                                              )
                                              .map((staff) => (
                                                <option
                                                  key={staff.id}
                                                  value={staff.id}
                                                >
                                                  {staff.name}
                                                  {staff.role
                                                    ? ` - ${staff.role}`
                                                    : ""}
                                                </option>
                                              ))}
                                          </select>
                                        </div>

                                        {escalationMethod[note.id] !==
                                          "in-person" && (
                                          <div>
                                            <label
                                              style={{
                                                fontSize: "0.8rem",
                                                fontWeight: "600",
                                                marginBottom: "5px",
                                                display: "block",
                                              }}
                                            >
                                              Add a Note:
                                            </label>
                                            <textarea
                                              name="assignNotes"
                                              className="form-control form-control-sm"
                                              rows="3"
                                              style={{ fontSize: "0.8rem" }}
                                              maxLength={500}
                                              value={
                                                (escalationNote &&
                                                  escalationNote[note.id]) ??
                                                ""
                                              }
                                              onChange={(e) => {
                                                if (e && e.target) {
                                                  const selectedValue =
                                                    e.target.value;
                                                  setEscalationNote((prev) => ({
                                                    ...prev,
                                                    [note.id]: selectedValue,
                                                  }));
                                                }
                                              }}
                                              disabled={!!note.reviewCode}
                                            ></textarea>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                  {/* Regular Note Input - Show for Uphold/Override */}
                                  {(!note.reviewCode ||
                                    note.reviewCode === "ESCALATE") &&
                                    reviewSelections[note.id] &&
                                    reviewSelections[note.id] !==
                                      "escalate" && (
                                      <>
                                        <div style={{ marginTop: "15px" }}>
                                          <label
                                            style={{
                                              fontSize: "0.8rem",
                                              fontWeight: "600",
                                            }}
                                          >
                                            Add a Note:
                                          </label>
                                          <textarea
                                            className="form-control form-control-sm"
                                            rows="2"
                                            style={{ fontSize: "0.8rem" }}
                                            maxLength={500}
                                            value={reviewNotes[note.id] ?? ""}
                                            onChange={(e) =>
                                              handleReviewNoteChange(
                                                note.id,
                                                e.target.value
                                              )
                                            }
                                          ></textarea>
                                        </div>
                                      </>
                                    )}

                                  {/* Update Button - Show for all selections when not reviewed, but hide when escalate is selected */}
                                  {reviewSelections[note.id] !== "escalate" &&
                                    reviewSelections[note.id] && (
                                      <div
                                        style={{
                                          marginTop: "10px",
                                          textAlign: "right",
                                        }}
                                      >
                                        <button
                                          className="btn btn-sm btn-success"
                                          disabled={!isUpdateEnabled(note.id)}
                                          onClick={() =>
                                            handleUpdateReview(note.id)
                                          }
                                          style={{
                                            fontSize: "0.8rem",
                                            fontWeight: "600",
                                            opacity: isUpdateEnabled(note.id)
                                              ? 1
                                              : 0.5,
                                            cursor: isUpdateEnabled(note.id)
                                              ? "pointer"
                                              : "not-allowed",
                                          }}
                                        >
                                          Update reason code
                                        </button>
                                      </div>
                                    )}
                                  {note.reviewCode && note.reviewNotes && (
                                    <div
                                      style={{
                                        marginTop: "10px",
                                        padding: "10px",
                                        backgroundColor: "#f8f9fa",
                                        borderRadius: "4px",
                                      }}
                                    >
                                      <strong style={{ fontSize: "0.8rem" }}>
                                        {formatDate(note.reviewDate)}:
                                      </strong>
                                      <div
                                        style={{
                                          fontSize: "0.8rem",
                                          marginTop: "5px",
                                        }}
                                      >
                                        {note.reviewNotes}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                  </div>
                </div>

                {/* Right Column - Application Details */}
                <div className="col-6">
                  <div
                    style={{
                      padding: "15px",
                    }}
                  >
                    <h6
                      style={{
                        fontWeight: "600",
                        color: "#333",
                        marginBottom: "15px",
                      }}
                    >
                      APPLICATION DETAILS - VOLUNTEERED BY THE APPLICANT
                    </h6>

                    {/* Expandable Sections */}
                    {[
                      {
                        key: "letsBegin",
                        title: "LET'S BEGIN",
                        fields: [
                          "Retain branch:",
                          "Statements preference:",
                          "Reason for opening:",
                        ],
                      },
                      { key: "aboutYou", title: "ABOUT YOU", fields: [] },
                      { key: "employment", title: "EMPLOYMENT", fields: [] },
                      { key: "taxInfo", title: "TAX INFO", fields: [] },
                      { key: "compliance", title: "COMPLIANCE", fields: [] },
                      {
                        key: "jointApplicants",
                        title: "JOINT APPLICANTS",
                        fields: [],
                      },
                      { key: "debitCard", title: "DEBIT CARD", fields: [] },
                      {
                        key: "accountInfo",
                        title: "ACCOUNT INFO",
                        fields: [],
                      },
                      {
                        key: "overdraftProtection",
                        title: "OVERDRAFT PROTECTION",
                        fields: [],
                      },
                    ].map((section) => (
                      <div
                        key={section.key}
                        style={{
                          backgroundColor: "white",
                          border: "1px solid #dee2e6",
                          borderRadius: "4px",
                          marginBottom: "10px",
                        }}
                      >
                        <div
                          onClick={() => toggleSection(section.key)}
                          style={{
                            padding: "12px 15px",
                            cursor: "pointer",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{ fontWeight: "600", fontSize: "0.85rem" }}
                          >
                            {section.title}
                          </span>
                          <button
                            className="btn btn-link p-0"
                            style={{ color: "#dc3545", fontSize: "0.8rem" }}
                          >
                            View <i className="fas fa-chevron-right ms-1"></i>
                          </button>
                        </div>
                        {expandedSections[section.key] && (
                          <div
                            style={{
                              padding: "15px",
                              borderTop: "1px solid #dee2e6",
                              fontSize: "0.85rem",
                              color: "#666",
                            }}
                          >
                            {section.key === "letsBegin" && (
                              <>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Retain branch:</strong>{" "}
                                  {getNestedValue(applicant, "member.branch")}
                                </div>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Statements preference:</strong>{" "}
                                  {getNestedValue(
                                    applicant,
                                    "regulatory.mailStmt"
                                  )
                                    ? "Paper"
                                    : "Email"}
                                </div>
                                <div>
                                  <strong>Reason for opening:</strong>{" "}
                                  {getNestedValue(
                                    applicant,
                                    "member.reasonOpened"
                                  )}
                                </div>
                              </>
                            )}
                            {section.key === "aboutYou" && (
                              <>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Full Name:</strong>{" "}
                                  {getNestedValue(applicant, "member.name")}{" "}
                                  {getNestedValue(
                                    applicant,
                                    "member.middleName",
                                    ""
                                  )}{" "}
                                  {getNestedValue(
                                    applicant,
                                    "member.surname",
                                    ""
                                  )}
                                </div>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Date of Birth:</strong>{" "}
                                  {formatDate(
                                    getNestedValue(applicant, "member.dob")
                                  )}
                                </div>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Address:</strong>{" "}
                                  {formatAddress(
                                    getNestedValue(applicant, "address", null)
                                  )}
                                </div>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Mail:</strong>{" "}
                                  {applicant.mailingSameAsCivic
                                    ? formatAddress(
                                        getNestedValue(
                                          applicant,
                                          "address",
                                          null
                                        )
                                      )
                                    : formatMailAddress(
                                        getNestedValue(
                                          applicant,
                                          "address",
                                          null
                                        )
                                      )}
                                </div>
                              </>
                            )}
                            {section.key === "employment" && (
                              <>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Employment Status:</strong>{" "}
                                  {getDescriptionFromArray(
                                    employmentAndIncomeReducer.statusList,
                                    getNestedValue(
                                      applicant,
                                      "employment.employmentStatus"
                                    )
                                  ) || "—"}
                                </div>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Occupation:</strong>{" "}
                                  {getNestedValue(
                                    applicant,
                                    "employment.jobTitle"
                                  )}
                                </div>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Position:</strong>{" "}
                                  {getNestedValue(
                                    applicant,
                                    "employment.employerPosition"
                                  )}
                                </div>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Employer Name:</strong>{" "}
                                  {getNestedValue(
                                    applicant,
                                    "employment.currentEmployer"
                                  )}
                                </div>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Employer Contact No:</strong>{" "}
                                  {formatPhone(
                                    getNestedValue(
                                      applicant,
                                      "employment.employerPhoneArea"
                                    ) +
                                      getNestedValue(
                                        applicant,
                                        "employment.employerPhone"
                                      )
                                  )}
                                </div>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Monthly Income:</strong>{" "}
                                  {getNestedValue(
                                    applicant,
                                    "employment.employerSalary"
                                  ) !== "—"
                                    ? "$" +
                                      getNestedValue(
                                        applicant,
                                        "employment.employerSalary"
                                      )
                                    : "—"}
                                </div>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Source of Income:</strong>{" "}
                                  {getDescriptionFromArray(
                                    employmentAndIncomeReducer.incomeSourcesList,
                                    getNestedValue(
                                      applicant,
                                      "employment.incomeType"
                                    )
                                  ) || "—"}
                                </div>
                              </>
                            )}
                            {section.key === "taxInfo" && (
                              <>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>SIN:</strong>{" "}
                                  {getNestedValue(applicant, "member.sin")
                                    ? "*****" +
                                      getNestedValue(
                                        applicant,
                                        "member.sin"
                                      ).slice(-4)
                                    : "—"}
                                </div>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Other tax residence (TIN):</strong>{" "}
                                  {applicant
                                    ? hasOtherTaxResidence
                                      ? "Yes"
                                      : "No"
                                    : "—"}
                                </div>
                                {hasUsaTaxResidence && (
                                  <div style={{ marginBottom: "8px" }}>
                                    <strong>USA:</strong>{" "}
                                    {applicant?.regulatory?.ssn || "—"}
                                  </div>
                                )}
                                {hasOtherCountryTaxResidence && (
                                  <div style={{ marginBottom: "8px" }}>
                                    <strong>
                                      {oecdTaxResidency?.Value ||
                                        "Other Country"}
                                      :
                                    </strong>{" "}
                                    {oecdTaxId?.Value || "—"}
                                  </div>
                                )}
                              </>
                            )}
                            {section.key === "compliance" && (
                              <>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>PEP / HIO:</strong>{" "}
                                  {(applicant?.peps &&
                                    applicant.peps.length > 0) ||
                                  applicant?.regulatory?.memberIsPedp ||
                                  applicant?.regulatory?.memberIsPefp ||
                                  applicant?.regulatory?.memberIsHio
                                    ? "Yes"
                                    : "No"}
                                </div>
                                {applicant?.peps && applicant.peps.length > 0 && (
                                  <>
                                    {applicant.peps.map((pep, index) => (
                                      <div
                                        key={index}
                                        className="[ row ] [ details-section__item ]"
                                      >
                                        <div
                                          className="[ col-6 ] [ details-section__item-label ]"
                                          style={{ paddingLeft: "30px" }}
                                        >
                                          Who:
                                        </div>
                                        <div className="[ col-6 ] [ details-section__item-description ]">
                                          {pep.name || "—"}
                                        </div>
                                        <div
                                          className="[ col-6 ] [ details-section__item-label ]"
                                          style={{ paddingLeft: "30px" }}
                                        >
                                          Jurisdiction:
                                        </div>
                                        <div className="[ col-6 ] [ details-section__item-description ]">
                                          {pep.jurisdiction || "—"}
                                        </div>
                                        <div
                                          className="[ col-6 ] [ details-section__item-label ]"
                                          style={{ paddingLeft: "30px" }}
                                        >
                                          Source of funds:
                                        </div>
                                        <div className="[ col-6 ] [ details-section__item-description ]">
                                          {pep.fundsSource || "—"}
                                        </div>
                                      </div>
                                    ))}
                                  </>
                                )}
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Banking relationship:</strong>{" "}
                                  {complianceReducer.accountPurposeList.find(
                                    (item) =>
                                      item.code ===
                                      applicant?.member?.intendedUseOfMembership
                                  )?.description || "—"}
                                </div>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Third party:</strong>{" "}
                                  {applicant?.thirdParties &&
                                  applicant.thirdParties.length > 0
                                    ? "Yes"
                                    : "No"}
                                </div>
                                {applicant?.thirdParties &&
                                  applicant.thirdParties.length > 0 && (
                                    <>
                                      {applicant.thirdParties.map(
                                        (party, index) => (
                                          <div
                                            key={index}
                                            className="[ row ] [ details-section__item ]"
                                          >
                                            <div
                                              className="[ col-6 ] [ details-section__item-label ]"
                                              style={{ paddingLeft: "30px" }}
                                            >
                                              Name:
                                            </div>
                                            <div className="[ col-6 ] [ details-section__item-description ]">
                                              {party.name || "—"}
                                            </div>
                                            <div
                                              className="[ col-6 ] [ details-section__item-label ]"
                                              style={{ paddingLeft: "30px" }}
                                            >
                                              Relationship:
                                            </div>
                                            <div className="[ col-6 ] [ details-section__item-description ]">
                                              {party.relationship || "—"}
                                            </div>
                                          </div>
                                        )
                                      )}
                                    </>
                                  )}
                              </>
                            )}
                            {section.key === "jointApplicants" && (
                              <>
                                {Array.isArray(applicationProperty?.slots) &&
                                applicationProperty.slots.length > 1 ? (
                                  applicationProperty.slots
                                    .filter((app) => app.type === "co")
                                    ?.map((joint, idx) => (
                                      <div
                                        key={idx}
                                        style={{
                                          marginBottom: "15px",
                                          paddingBottom: "15px",
                                          borderBottom:
                                            idx <
                                            applicationProperty.slots.filter(
                                              (app) => app.type === "co"
                                            ).length -
                                              1
                                              ? "1px solid #dee2e6"
                                              : "none",
                                        }}
                                      >
                                        <div
                                          style={{
                                            marginBottom: "8px",
                                            fontWeight: "600",
                                          }}
                                        >
                                          Co-Applicant {idx + 1}
                                        </div>
                                        <div style={{ marginBottom: "8px" }}>
                                          <strong>Email:</strong>{" "}
                                          {getNestedValue(joint, "extra.email")}
                                        </div>
                                        <div style={{ marginBottom: "8px" }}>
                                          <strong>Phone:</strong>{" "}
                                          {joint.extra.phoneNumber}
                                        </div>
                                        <div style={{ marginBottom: "8px" }}>
                                          <strong>In session:</strong>{" "}
                                          {joint.extra.inSession ? "Yes" : "No"}
                                        </div>
                                      </div>
                                    ))
                                ) : (
                                  <p style={{ margin: 0, fontStyle: "italic" }}>
                                    No joint applicants
                                  </p>
                                )}
                              </>
                            )}
                            {section.key === "debitCard" && (
                              <>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Debit Card Requested:</strong>{" "}
                                  {applicationProperty?.doximProducts?.some(
                                    (product) => product.type === "debitCard"
                                  )
                                    ? "Yes"
                                    : "No"}
                                </div>
                                {/* Commented out for phase 1 demos - ticket SIBM-380, remove comments later - CH */}
                                {/* <div style={{ marginBottom: "8px" }}>
                                  <strong>Card Type:</strong>{" "}
                                  {getNestedValue(
                                    applicant,
                                    "debitCard.cardType"
                                  )}
                                </div>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Delivery Method:</strong>{" "}
                                  {getNestedValue(
                                    applicant,
                                    "debitCard.deliveryMethod"
                                  )}
                                </div> */}
                              </>
                            )}
                            {section.key === "accountInfo" && (
                              <>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Intended Use:</strong>{" "}
                                  {selectedProductInfo?.udfList
                                    ? selectedProductInfo?.udfList[
                                        getProductInfoFromCurrentDoximProducts(
                                          applicationProperty.doximProducts,
                                          productList
                                        )?.intendedUse
                                      ]
                                    : "—"}
                                </div>
                              </>
                            )}
                            {section.key === "overdraftProtection" && (
                              <>
                                <div style={{ marginBottom: "8px" }}>
                                  <strong>Overdraft Protection:</strong>{" "}
                                  {selectedProductInfo?.udfList
                                    ? selectedProductInfo?.udfList[
                                        finInfo?.overdraftTermsAndConditionsFlag
                                      ] || "—"
                                    : "—"}
                                </div>
                              </>
                            )}
                            {![
                              "letsBegin",
                              "aboutYou",
                              "employment",
                              "taxInfo",
                              "compliance",
                              "jointApplicants",
                              "debitCard",
                              "accountInfo",
                              "overdraftProtection",
                            ].includes(section.key) && (
                              <p style={{ margin: 0, fontStyle: "italic" }}>
                                No data available for this section
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="col-12">
                  <div className="[ divider ]" />
                  <div
                    style={{
                      padding: "20px",
                      display: "flex",
                      justifyContent: "center",
                      gap: "15px",
                    }}
                  >
                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleRejectApplication}
                      disabled={!allNotesUphold() || !application.inReview}
                      style={{
                        backgroundColor: "#17a2b8",
                        color: "white",
                        padding: "10px 30px",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                        borderRadius: "4px",
                        opacity:
                          allNotesUphold() && application.inReview ? 1 : 0.5,
                        cursor:
                          allNotesUphold() && application.inReview
                            ? "pointer"
                            : "not-allowed",
                      }}
                    >
                      REJECT APPLICATION
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleEscalateApplication}
                      disabled={!hasEscalatedNote() || !application.inReview}
                      style={{
                        backgroundColor: "#17a2b8",
                        color: "white",
                        padding: "10px 30px",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                        borderRadius: "4px",
                        opacity: hasEscalatedNote() ? 1 : 0.5,
                        cursor: hasEscalatedNote() ? "pointer" : "not-allowed",
                      }}
                    >
                      ESCALATE APPLICATION
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        const applicationId =
                          application.id || application.applicationId;
                        if (doConfirmApp && applicationId) {
                          doConfirmApp(
                            {
                              applicationId,
                              reApply: true,
                            },
                            (res) => {
                              doGetVault(
                                { vaultUrl: applicationId },
                                (vault) => {
                                  if (vault.applicationId === applicationId) {
                                    dispatch(
                                      getProductTemplate((resTemplate) => {
                                        console.log("resTemplate", resTemplate);
                                        vault.applicationStep = {
                                          index:
                                            getCurrentStepIndexFromTemplateFlowByStep(
                                              resTemplate,
                                              vault.templateId
                                            ) || 10,
                                          componentName:
                                            AppConstants.APPLICATIONSTEP
                                              .CONFIRM,
                                        };
                                        doGetApplicationProperty(
                                          { applicationId: applicationId },
                                          (applicationProperty) => {
                                            if (applicationProperty?.inReview) {
                                              // Show success modal
                                              setShowApplicationSubmittedModal(
                                                true
                                              );
                                            } else {
                                              doGetProductFromVault((res) => {
                                                if (
                                                  res.status === HTTP_STATUS.OK
                                                ) {
                                                  const productVault = [];
                                                  vault.isForResubmission = true;
                                                  if (
                                                    Array.isArray(res.data) &&
                                                    res.data.length > 0
                                                  ) {
                                                    productVault.push(vault);
                                                    productVault.push(
                                                      ...res.data
                                                    );
                                                  } else {
                                                    productVault.push(vault);
                                                  }
                                                  doUpdateProductToVault(
                                                    productVault,
                                                    () => {
                                                      history.push(
                                                        ROUTES.CONFIRM_ACCOUNT_CREATE +
                                                          "?" +
                                                          AppConstants.PARAM
                                                            .APP_RESUBMIT
                                                      );
                                                    }
                                                  );
                                                }
                                              });
                                            }
                                          }
                                        );
                                      }, vault)
                                    );
                                  }
                                }
                              );
                            }
                          );
                        }
                      }}
                      disabled={!allNotesReviewed() || !application.inReview}
                      style={{
                        backgroundColor: "#17a2b8",
                        color: "white",
                        padding: "10px 30px",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                        borderRadius: "4px",
                      }}
                    >
                      RE-SUBMIT APPLICATION
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Success Modal */}
      <PopupModal
        showModal={showSuccessModal}
        modalType={AppConstants.MODALTYPE.SUCCESS}
        type="Success"
        title="File Uploaded Successfully"
        description="Your document has been uploaded successfully."
        btnText="OK"
        toggleModal={() => setShowSuccessModal(false)}
        popupBtnClick={() => setShowSuccessModal(false)}
        closeBtnClick={() => setShowSuccessModal(false)}
        showPopupModalButtons={true}
        showPopupHeaderCloseButton={true}
      />

      {/* Delete Success Modal */}
      <PopupModal
        showModal={showDeleteSuccessModal}
        modalType={AppConstants.MODALTYPE.SUCCESS}
        type="Success"
        title="File Deleted Successfully"
        description="Your document has been deleted successfully."
        btnText="OK"
        toggleModal={() => setShowDeleteSuccessModal(false)}
        popupBtnClick={() => setShowDeleteSuccessModal(false)}
        closeBtnClick={() => setShowDeleteSuccessModal(false)}
        showPopupModalButtons={true}
        showPopupHeaderCloseButton={true}
      />

      {/* Escalate Success Modal */}
      <PopupModal
        showModal={showEscalateSuccessModal}
        modalType={AppConstants.MODALTYPE.SUCCESS}
        type="Success"
        title="Success!"
        description={`A task to review and adjudicate this application has been created successfully and assigned to ${escalatedColleagueName}.`}
        description2={
          "Hit 'OK' be redirected back to member profile applications tab."
        }
        btnText="OK"
        toggleModal={() => {
          setShowEscalateSuccessModal(false);
          if (onBack) {
            onBack();
          }
        }}
        popupBtnClick={() => {
          setShowEscalateSuccessModal(false);
          if (onBack) {
            onBack();
          }
        }}
        closeBtnClick={() => {
          setShowEscalateSuccessModal(false);
          if (onBack) {
            onBack();
          }
        }}
        showPopupModalButtons={true}
        showPopupHeaderCloseButton={true}
      />

      {/* Application Submitted Modal */}
      <PopupModal
        showModal={showApplicationSubmittedModal}
        modalType={AppConstants.MODALTYPE.SUCCESS}
        type="Success"
        description="Application submitted"
        btnText="OK"
        toggleModal={handleApplicationSubmittedModalClose}
        popupBtnClick={handleApplicationSubmittedModalClose}
        closeBtnClick={handleApplicationSubmittedModalClose}
        showPopupModalButtons={true}
        showPopupHeaderCloseButton={true}
      />

      {/* Update Notes Modal (shown ONLY after in-person verification) */}
      <Modal
        show={showUpdateNotesModal && !showVerificationModal}
        onHide={() => setShowUpdateNotesModal(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header
          closeButton
          style={{
            padding: "20px",
          }}
        >
          <Modal.Title style={{ fontWeight: "600", fontSize: "1.1rem" }}>
            Adjudicate Reason Code Failure
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "30px 20px" }}>
          <p
            style={{
              marginBottom: "20px",
              fontSize: "0.9rem",
              color: "#333",
              lineHeight: "1.5",
            }}
          >
            {getVerificationColleagueName()}, how would you like to adjudicate
            this failed reason code?
          </p>

          {/* Display notes requiring review */}
          {applicationNotes
            .filter((note) => {
              // Show notes that are selected for escalation with in-person method
              return (
                reviewSelections[note.id] === "escalate" &&
                escalationMethod[note.id] === "in-person"
              );
            })
            .map((note, index) => (
              <div
                key={note.id || index}
                style={{
                  backgroundColor: "#fff5f5",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  marginBottom: "20px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#fff5f5",
                    padding: "12px",
                  }}
                >
                  <div style={{ marginBottom: "8px" }}>
                    <span
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: "600",
                        color: "red",
                      }}
                    >
                      STATUS: Needs review
                    </span>
                  </div>
                  {note.code && (
                    <div
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: "600",
                        marginBottom: "4px",
                      }}
                    >
                      Error Code: {note.code}
                    </div>
                  )}
                  {note.summary && (
                    <div
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: "600",
                      }}
                    >
                      {note.summary}
                    </div>
                  )}
                </div>
                <div style={{ padding: "15px" }}>
                  {note.details && (
                    <div
                      style={{
                        fontSize: "0.85rem",
                        color: "#666",
                        marginBottom: "15px",
                      }}
                    >
                      {note.details}
                    </div>
                  )}

                  <div
                    style={{
                      marginBottom: "10px",
                      fontStyle: "italic",
                      fontSize: "0.85rem",
                    }}
                  >
                    I have reviewed this and I find it appropriate to:
                  </div>
                  <div className="d-flex mb-3 justify-content-center">
                    <button
                      className={`btn btn-sm mx-2 ${
                        modalReviewSelections[note.id] === "uphold"
                          ? "btn-dark"
                          : "btn-outline-secondary"
                      }`}
                      onClick={() =>
                        setModalReviewSelections((prev) => ({
                          ...prev,
                          [note.id]: "uphold",
                        }))
                      }
                    >
                      Uphold
                    </button>
                    <button
                      className={`btn btn-sm mx-2 ${
                        modalReviewSelections[note.id] === "override"
                          ? "btn-dark"
                          : "btn-outline-secondary"
                      }`}
                      onClick={() =>
                        setModalReviewSelections((prev) => ({
                          ...prev,
                          [note.id]: "override",
                        }))
                      }
                    >
                      Override
                    </button>
                    <button
                      className={`btn btn-sm mx-2 ${
                        modalReviewSelections[note.id] === "escalate"
                          ? "btn-dark"
                          : "btn-outline-secondary"
                      }`}
                      disabled={true}
                    >
                      Escalate
                    </button>
                  </div>

                  {/* Note Input - Always show for Uphold/Override/Escalate */}
                  <div style={{ marginTop: "15px" }}>
                    <label
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        marginBottom: "5px",
                        display: "block",
                      }}
                    >
                      Add a Note:
                    </label>
                    <textarea
                      className="form-control form-control-sm"
                      rows="3"
                      style={{ fontSize: "0.8rem" }}
                      maxLength={500}
                      value={modalReviewNotes[note.id] ?? ""}
                      onChange={(e) => {
                        const textValue = e.target.value ? e.target.value : "";
                        setModalReviewNotes((prev) => ({
                          ...prev,
                          [note.id]: textValue,
                        }));
                      }}
                    ></textarea>
                  </div>
                </div>
              </div>
            ))}
        </Modal.Body>
        <Modal.Footer
          style={{
            padding: "15px 20px",
            justifyContent: "space-between",
          }}
        >
          <button
            className="btn btn-outline-secondary"
            onClick={() => setShowUpdateNotesModal(false)}
            style={{
              padding: "8px 20px",
              fontSize: "0.9rem",
              fontWeight: "600",
            }}
          >
            CANCEL
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              console.log("escalating", selectedColleague);
              // Find notes that were originally selected for in-person escalation
              // and now have uphold or override selected with notes in modal state
              const notesToUpdate = applicationNotes.filter((note) => {
                // Check if this note was originally selected for in-person escalation
                const wasInPersonEscalation =
                  escalationMethod[note.id] === "in-person";

                // Now check if uphold or override is selected with notes entered in modal
                const selection = modalReviewSelections[note.id];
                const hasValidSelection =
                  (selection === "uphold" || selection === "override") &&
                  modalReviewNotes[note.id]?.trim();

                return wasInPersonEscalation && hasValidSelection;
              });

              // Update each note via API using modal state
              if (notesToUpdate.length > 0) {
                let completedUpdates = 0;
                const totalUpdates = notesToUpdate.length;

                notesToUpdate.forEach((note) => {
                  const selection = modalReviewSelections[note.id];
                  const noteText = modalReviewNotes[note.id];

                  const payload = {
                    reviewCode: selection.toUpperCase(),
                    reviewNotes: noteText,
                    reviewBy: selectedColleague[note.id],
                  };

                  doUpdateApplicationNotesReview(
                    note.id,
                    payload,
                    (response) => {
                      console.log("Update review response:", response);
                      completedUpdates++;

                      // When all updates are complete, close modal and refresh
                      if (completedUpdates === totalUpdates) {
                        // Refresh application notes
                        if (
                          doGetApplicationNotes &&
                          (application.id || application.applicationId)
                        ) {
                          doGetApplicationNotes(
                            application.id || application.applicationId,
                            (result) => {
                              setApplicationNotes(result?.data || result || []);

                              // Clear modal state
                              setModalReviewSelections({});
                              setModalReviewNotes({});

                              // Clear main state (escalation-related)
                              setReviewSelections({});
                              setReviewNotes({});
                              setEscalationMethod({});
                              setSelectedColleague({});
                              setEscalationNote({});

                              // Close modal
                              setShowUpdateNotesModal(false);
                            }
                          );
                        } else {
                          // Clear modal state and close
                          setModalReviewSelections({});
                          setModalReviewNotes({});

                          // Clear main state (escalation-related)
                          setReviewSelections({});
                          setReviewNotes({});
                          setEscalationMethod({});
                          setSelectedColleague({});
                          setEscalationNote({});

                          setShowUpdateNotesModal(false);
                        }
                      }
                    }
                  );
                });
              } else {
                setShowUpdateNotesModal(false);
              }
            }}
            disabled={
              !applicationNotes.some((note) => {
                // Check if this note was originally selected for in-person escalation
                const wasInPersonEscalation =
                  escalationMethod[note.id] === "in-person";

                // Now check if uphold or override is selected with notes entered in modal
                const selection = modalReviewSelections[note.id];
                const hasValidSelection =
                  (selection === "uphold" || selection === "override") &&
                  modalReviewNotes[note.id]?.trim();

                return wasInPersonEscalation && hasValidSelection;
              })
            }
            style={{
              padding: "8px 20px",
              fontSize: "0.9rem",
              fontWeight: "600",
              opacity: applicationNotes.some((note) => {
                // Check if this note was originally selected for in-person escalation
                const wasInPersonEscalation =
                  escalationMethod[note.id] === "in-person";

                // Now check if uphold or override is selected with notes entered in modal
                const selection = modalReviewSelections[note.id];
                const hasValidSelection =
                  (selection === "uphold" || selection === "override") &&
                  modalReviewNotes[note.id]?.trim();

                return wasInPersonEscalation && hasValidSelection;
              })
                ? 1
                : 0.5,
            }}
          >
            UPDATE REASON CODE
          </button>
        </Modal.Footer>
      </Modal>

      {/* Verification Code Modal */}
      <OtpModal
        show={showVerificationModal}
        onHide={handleVerificationClose}
        title="Authenticator"
        description={`In order to proceed with in-session reason code escalation and adjudication, ${getVerificationColleagueName()}, will need to enter their authorization code.`}
        onVerify={handleVerificationSubmit}
        verifyButtonText="VERIFY"
        verifyingButtonText="VERIFYING..."
        showResendCode={true}
        onResendCode={() => {
          // Handle resend code logic here if needed
          console.log("Resend code requested");
        }}
        errors={verificationErrors}
        verifyDisabled={false}
      />
    </div>
  );
};

export default ApplicationDetails;
